import { useEffect, useMemo, useRef, useState } from 'react';
import { X, MapPin, Package, CalendarDays } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGoogleMapsScript } from '../../hooks/useGoogleMapsScript';

const extractLatLng = trip => {
  const origin = trip?.originDetails;
  const destination = trip?.destinationDetails;

  if (
    origin &&
    typeof origin.latitude === 'number' &&
    typeof origin.longitude === 'number'
  ) {
    return {
      lat: origin.latitude,
      lng: origin.longitude,
      type: 'origin',
    };
  }

  if (
    destination &&
    typeof destination.latitude === 'number' &&
    typeof destination.longitude === 'number'
  ) {
    return {
      lat: destination.latitude,
      lng: destination.longitude,
      type: 'destination',
    };
  }

  return null;
};

export function TripsMapModal({ visible, onClose, trips = [], title }) {
  const { ready } = useGoogleMapsScript();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const locations = useMemo(
    () =>
      trips
        .map(trip => {
          const coords = extractLatLng(trip);
          if (!coords) return null;
          return { trip, coords };
        })
        .filter(Boolean),
    [trips],
  );

  const selectedTrip = useMemo(
    () =>
      locations.find(item => item.trip.id === selectedTripId) ||
      locations[0] ||
      null,
    [locations, selectedTripId],
  );

  useEffect(() => {
    if (!visible) {
      clearMarkers();
      setSelectedTripId(null);
      return;
    }

    if (!ready || !mapRef.current) {
      return;
    }

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 39.925533, lng: 32.866287 },
        zoom: 4,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
      });
    }

    clearMarkers();
    if (locations.length === 0) {
      setSelectedTripId(null);
    } else if (!selectedTripId) {
      setSelectedTripId(locations[0].trip.id);
    }

    if (locations.length === 0) {
      mapInstanceRef.current.setCenter({ lat: 39.925533, lng: 32.866287 });
      mapInstanceRef.current.setZoom(4);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    markersRef.current = locations.map(({ trip, coords }) => {
      const marker = new window.google.maps.Marker({
        position: coords,
        map: mapInstanceRef.current,
        label: {
          text:
            trip.originDetails?.city?.[0] ||
            trip.destinationDetails?.city?.[0] ||
            'F',
          color: '#111827',
          fontSize: '12px',
          fontWeight: '700',
        },
      });

      marker.addListener('click', () => {
        setSelectedTripId(trip.id);
      });

      bounds.extend(coords);
      return marker;
    });

    if (!bounds.isEmpty()) {
      mapInstanceRef.current.fitBounds(bounds);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, ready, locations]);

  useEffect(() => {
    if (!ready || !selectedTrip || !mapInstanceRef.current) return;
    const { coords } = selectedTrip;
    mapInstanceRef.current.panTo(coords);
    mapInstanceRef.current.setZoom(6);
  }, [selectedTrip, ready]);

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur">
      <div className="absolute inset-0">
        <div className="absolute left-1/2 top-6 z-10 flex w-[90%] max-w-4xl -translate-x-1/2 items-center justify-between rounded-2xl bg-white/90 px-6 py-3 shadow-2xl dark:bg-slate-900/90">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.4em] text-yellow-500">
              {t('tripsMap.mapPreview')}
            </p>
            <h3 className="text-lg font-black text-gray-900 dark:text-white">
              {title || t('tripsMap.defaultTitle')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              {t('tripsMap.showingLocations', { count: locations.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-200 bg-white p-2 text-gray-600 shadow-lg transition hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div ref={mapRef} className="h-full w-full" />
        {selectedTrip && (
          <div className="pointer-events-auto absolute bottom-8 left-1/2 z-20 w-[90%] max-w-xl -translate-x-1/2 rounded-3xl bg-white/95 p-6 shadow-2xl dark:bg-slate-900/95">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.4em] text-gray-500 dark:text-slate-400">
                  {t('tripsMap.selectedTrip')}
                </p>
                <h4 className="text-lg font-black text-gray-900 dark:text-white">
                  {selectedTrip.trip.createdByName ||
                    t('tripCard.flexyUser')}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (selectedTrip.trip.id) {
                    navigate(`/trips/detail/${selectedTrip.trip.id}`);
                  }
                }}
                className="rounded-2xl border border-gray-900 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-100 dark:border-slate-200 dark:text-slate-100 dark:hover:bg-slate-800"
              >
                {t('tripsMap.viewDetails')}
              </button>
            </div>
            <div className="mt-4 grid gap-3 text-sm text-gray-700 dark:text-slate-300 md:grid-cols-2">
              <div className="flex items-start gap-2">
                <MapPin className="mt-1 h-4 w-4 text-gray-400 dark:text-slate-500" />
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-slate-400">
                    {t('tripsMap.route')}
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {selectedTrip.trip.originCity ||
                      selectedTrip.trip.originDetails?.city ||
                      t('home.fromFallback')}
                    ,{' '}
                    {selectedTrip.trip.originCountry ||
                      selectedTrip.trip.originDetails?.country ||
                      ''}
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    →
                    {selectedTrip.trip.destinationCity ||
                      selectedTrip.trip.destinationDetails?.city ||
                      t('home.toFallback')}
                    ,{' '}
                    {selectedTrip.trip.destinationCountry ||
                      selectedTrip.trip.destinationDetails?.country ||
                      ''}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CalendarDays className="mt-1 h-4 w-4 text-gray-400 dark:text-slate-500" />
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-slate-400">
                    {t('travelerTripRequests.departure')}
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {selectedTrip.trip.departureDate ||
                      t('tripCard.scheduleTbd')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Package className="mt-1 h-4 w-4 text-gray-400 dark:text-slate-500" />
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-slate-400">
                    {t('tripsMap.capacity')}
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {selectedTrip.trip.availableSpace
                      ? `${selectedTrip.trip.availableSpace} ${
                          selectedTrip.trip.spaceUnit || 'kg'
                        }`
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
